/**
 * The ONE precedence rule for merging `brand_kits.brand_guidelines`
 * (canonical) with a `user_brand_voices` row (legacy): canonical wins when
 * it carries a real value, legacy fills the gap. Extracted 2026-08-28 out of
 * `brand-context-loader.ts`'s `mergeVoiceOverlay` (previously a private,
 * inline implementation) so `BrandSettings.tsx`'s read-side merge can share
 * the exact same rule instead of becoming a third divergent copy — see
 * `mergeVoiceOverlay`'s own doc comment for the cross-path parity contract
 * this also protects (`compose-brand-context.ts` is the edge-function twin;
 * it stays a separate implementation by necessity — Deno can't import from
 * `src/`).
 *
 * Pure, no I/O, framework-agnostic: safe to import from both server-capable
 * code (`brand-context-loader.ts`) and a plain React component.
 */

export function preferCanonicalText(
  canonical: string | null | undefined,
  legacy: string | null | undefined,
): string | undefined {
  if (typeof canonical === 'string' && canonical.trim().length > 0) return canonical;
  if (typeof legacy === 'string' && legacy.trim().length > 0) return legacy;
  return undefined;
}

export function preferCanonicalList<T>(
  canonical: readonly T[] | null | undefined,
  legacy: readonly T[] | null | undefined,
): T[] | undefined {
  if (Array.isArray(canonical) && canonical.length > 0) return canonical as T[];
  if (Array.isArray(legacy) && legacy.length > 0) return legacy as T[];
  return undefined;
}

/**
 * `user_brand_voices.content_pillars` HOLDS TWO DIFFERENT SHAPES. This flattens
 * both to names.
 *
 * 🔴 MEASURED IN PRODUCTION, 2026-09-09. The same jsonb column carries
 * `["Automated Creation & Brand Consistency", …]` on rows written by the
 * onboarding scan (`scan-url-for-brand.ts` stores a plain `string[]`) and
 * `[{"name":"Property Tax Reduction Strategies","description":""}, …]` on rows
 * written through the agent's brand-voice path (`scan-url-for-voice.ts` builds
 * `BrandVoiceProfile.contentPillars` as `{name,description}[]`). Three rows are
 * strings, one is objects, three are empty. ITC Tax — a real client — is the
 * object one.
 *
 * ⚠️ WHY THIS MATTERS, STATED PRECISELY SO NOBODY OVERSTATES IT. Every reader
 * types the column `string[]` (`compose-brand-context.ts:41`,
 * `regenerate-single-post/index.ts:385`, `BrandVoiceService.ts:34`), and
 * `generate-content-batch/index.ts:978` does
 * `contentPillars.join(', ')` STRAIGHT INTO THE PROMPT. An object array there
 * renders as `[object Object], [object Object]`.
 *
 * It is NOT happening today: every workspace with object-shaped pillars also
 * has canonical `brand_guidelines.pillars`, and `firstNonEmpty(canonical,
 * legacy)` picks canonical first, so the objects never reach the join. That is
 * luck, not design — clearing one canonical field, or the agent writing to a
 * workspace that has none, puts `[object Object]` in front of the model. This
 * is the shape-guard for that, not a fix for a live incident.
 *
 * ⚠️ The Deno edge functions cannot import this (no `src/` access from
 * `supabase/functions/`), so `compose-brand-context.ts` and
 * `regenerate-single-post` still read the raw column. They need their own copy.
 */
export function normalizeContentPillars(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (entry && typeof entry === 'object') {
        const name = (entry as { name?: unknown }).name;
        return typeof name === 'string' ? name.trim() : '';
      }
      return '';
    })
    .filter((name) => name.length > 0);
}
